'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { FEATURES, FeatureRenderer } from '@/components/hazel/features'
import { CloudScape, DeerMark, AnimatePresence } from '@/components/hazel/ui'

const OWNER_NAME = 'Hazel'
const BLINKIST_AMOUNT = 95000

export default function HazelAndFawn() {
  const [mounted, setMounted] = useState(false)
  const [now, setNow] = useState(new Date())
  const [balance, setBalance] = useState(0)
  const [blinkistClaimed, setBlinkistClaimed] = useState(false)
  const [active, setActive] = useState<string | null>(null)
  const [pulse, setPulse] = useState(false)

  // Load persisted state
  useEffect(() => {
    setMounted(true)
    try {
      const b = localStorage.getItem('hf_balance')
      setBalance(b ? parseFloat(b) : 1240)
      setBlinkistClaimed(localStorage.getItem('hf_blinkist') === 'true')
    } catch {
      setBalance(1240)
    }
  }, [])

  // Clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  const persistBalance = (val: number) => {
    setBalance(val)
    try { localStorage.setItem('hf_balance', String(val)) } catch {}
  }

  const addHundred = () => {
    persistBalance(balance + 100)
    setPulse(true)
    setTimeout(() => setPulse(false), 400)
  }

  const claimBlinkist = () => {
    if (blinkistClaimed) return
    persistBalance(balance + BLINKIST_AMOUNT)
    setBlinkistClaimed(true)
    try { localStorage.setItem('hf_blinkist', 'true') } catch {}
  }

  // Lock scroll while a feature is open
  useEffect(() => {
    document.body.style.overflow = active ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [active])

  if (!mounted) return <div className="min-h-screen bg-cream-50" />

  const hh = now.getHours()
  const greeting = hh < 12 ? 'Good morning' : hh < 18 ? 'Good afternoon' : 'Good evening'
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })

  return (
    <main className="relative min-h-screen overflow-x-hidden bg-gradient-to-b from-cream-50 via-cream-100 to-fawn-100 paper-grain text-fawn-700">
      <CloudScape tone="day" />

      {/* soft deer marks in the corners */}
      <DeerMark className="pointer-events-none fixed -left-4 bottom-4 h-40 w-40 opacity-10" color="#c4a385" />
      <DeerMark className="pointer-events-none fixed -right-6 top-24 h-32 w-32 rotate-12 opacity-10" color="#d9bfa3" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 py-8 md:px-12 md:py-10">
        {/* ============ HEADER ============ */}
        <header className="flex flex-col items-center text-center">
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-3">
            <span className="text-3xl">☁️</span>
            <h1 className="font-display text-5xl font-bold tracking-tight text-fawn-600 md:text-6xl">Hazel &amp; Fawn</h1>
            <span className="text-3xl">🦌</span>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-2 text-xl italic text-fawn-400">
            {greeting}, {OWNER_NAME}. Your calm, cozy corner of the world.
          </motion.p>
        </header>

        {/* ============ CLOCK + MONEY ROW ============ */}
        <section className="mt-8 grid gap-5 lg:grid-cols-3">
          {/* Large clock */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center justify-center rounded-[2rem] bg-white/70 p-8 shadow-cozy backdrop-blur lg:col-span-2"
          >
            <div className="font-display text-7xl font-bold tabular-nums tracking-tight text-fawn-700 md:text-8xl lg:text-9xl">
              {timeStr}
            </div>
            <div className="mt-2 text-2xl text-fawn-400">{dateStr}</div>
            <div className="mt-3 flex items-center gap-2 text-lg text-fawn-300">
              <span>🌤️</span><span>A gentle, unhurried day awaits you.</span>
            </div>
          </motion.div>

          {/* Balance + earning */}
          <div className="flex flex-col gap-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-[2rem] bg-white/70 p-7 shadow-cozy backdrop-blur"
            >
              <p className="text-sm uppercase tracking-[0.3em] text-fawn-400">Account Balance</p>
              <motion.div
                animate={pulse ? { scale: [1, 1.06, 1] } : {}}
                transition={{ duration: 0.4 }}
                className="mt-2 font-display text-5xl font-bold text-fawn-700"
              >
                ${balance.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </motion.div>
              <button
                onClick={addHundred}
                className="mt-4 w-full rounded-2xl bg-fawn-500 px-6 py-3.5 text-xl font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-fawn-600 active:translate-y-0"
              >
                + $100 🌸
              </button>
            </motion.div>

            {/* Blinkist earning */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-[2rem] bg-gradient-to-br from-moss-200 to-moss-300 p-7 shadow-cozy"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm uppercase tracking-[0.25em] text-fawn-600">Earnings · Blinkist 📚</p>
                <span className="text-2xl">🐝</span>
              </div>
              <div className="mt-2 font-display text-4xl font-bold text-fawn-700">${BLINKIST_AMOUNT.toLocaleString()}</div>
              <button
                onClick={claimBlinkist}
                disabled={blinkistClaimed}
                className={`mt-3 w-full rounded-2xl px-6 py-3 text-lg font-semibold shadow-soft transition-all ${
                  blinkistClaimed ? 'cursor-default bg-white/60 text-fawn-500' : 'bg-fawn-600 text-white hover:-translate-y-0.5 hover:bg-fawn-700'
                }`}
              >
                {blinkistClaimed ? 'Collected ✓' : 'Collect Earnings'}
              </button>
            </motion.div>
          </div>
        </section>

        {/* ============ FEATURE GRID ============ */}
        <section className="mt-10">
          <div className="mb-5 flex items-center justify-center gap-3 text-fawn-500">
            <span className="h-px w-16 bg-fawn-300" />
            <h2 className="font-display text-2xl">Your Meadow of Little Places</h2>
            <span className="h-px w-16 bg-fawn-300" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {FEATURES.map((f, i) => (
              <motion.button
                key={f.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 * i }}
                onClick={() => setActive(f.id)}
                className="group relative overflow-hidden rounded-3xl bg-white/70 p-6 text-left shadow-soft backdrop-blur transition-all hover:-translate-y-1 hover:shadow-cozy"
              >
                <div
                  className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-20 transition-transform group-hover:scale-125"
                  style={{ backgroundColor: f.accent }}
                />
                <div className="relative">
                  <div className="text-4xl">{f.emoji}</div>
                  <h3 className="mt-3 font-display text-2xl font-semibold" style={{ color: f.accent }}>{f.title}</h3>
                  <p className="mt-1 text-lg leading-snug text-fawn-500">{f.blurb}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-base font-medium text-fawn-400 transition-all group-hover:gap-2 group-hover:text-fawn-600">
                    Open <span>→</span>
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer className="mt-12 pb-6 text-center text-lg text-fawn-400">
          <p>Made with soft mornings, gentle deer, and quiet clouds. 🦌☁️🌷</p>
        </footer>
      </div>

      {/* ============ FULL-SCREEN FEATURE OVERLAY ============ */}
      <AnimatePresence mode="wait">
        {active && (
          <FeatureRenderer
            key={active}
            id={active}
            onBack={() => setActive(null)}
            name={OWNER_NAME}
            balance={balance}
          />
        )}
      </AnimatePresence>
    </main>
  )
}
